import { Meeting, MeetingId } from "features/Resources/Meeting/models";
import { useScheduleMeeting } from "features/Resources/Meeting/hooks";
import V2ModalForm from "components/ModalForm/V2ModalForm";
import ScheduleMeetingForm from "features/Resources/Meeting/Components/Form/ScheduleMeetingForm";
import { Text12 } from "components/Text";
import { meetingStartEnd } from "helpers/time";
import { Box, Button, Tooltip } from "@chakra-ui/react";
import { BaseScheduleTableButton } from "./BaseScheduleButton";

interface IndividualSchedulingButtonBaseProps {
  meetingId: MeetingId;
  renderButton: (onOpen: () => void) => React.ReactNode;
}

export function IndividualSchedulingButtonBase({ meetingId, renderButton }: IndividualSchedulingButtonBaseProps) {
  const { mutate: onScheduleMeeting, isPending: isScheduling } = useScheduleMeeting(meetingId);
  return (
    <V2ModalForm
      modalHeader="Schedule Meeting"
      size="2xl"
      closeOnOverlayClick
      includeCloseButton={false}
      renderButton={renderButton}
      renderForm={(onClose) => (
        <ScheduleMeetingForm
          onComplete={onClose}
          meetingId={meetingId}
          onSubmit={(params) => onScheduleMeeting(params, { onSuccess: onClose })}
          submitIsLoading={isScheduling}
          submitButtonText="Schedule"
        />
      )}
    />
  );
}

interface IndividualSchedulingButtonProps {
  meeting: Meeting;
}

export function IndividualSchedulingTableButton({ meeting }: IndividualSchedulingButtonProps) {
  if (!meeting || ["removed", "skipped", "cancelled"].includes(meeting?.status)) {
    return null;
  }

  const isAutoScheduled = meeting.autoScheduled && !meeting.autoScheduleAcknowledgedAt;

  return (
    <IndividualSchedulingButtonBase
      renderButton={(onOpen) => {
        const button = meeting.status === "pending_scheduling" ? (
          <BaseScheduleTableButton onClick={onOpen} />
        ) : (
          <Button variant="unstyled" onClick={onOpen}>
            <Text12>{meetingStartEnd(meeting.startTime, meeting.endTime)}</Text12>
          </Button>
        );

        if (isAutoScheduled) {
          return (
            <Box display="flex" alignItems="center" gap="2">
              {button}
              <Tooltip label="This meeting was auto-scheduled. Ensure the time looks correct." hasArrow>
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="yellow.400"
                  border="1px solid"
                  borderColor="yellow.600"
                  cursor="pointer"
                />
              </Tooltip>
            </Box>
          );
        }

        return button;
      }}
      meetingId={meeting.id}
    />
  );
}
